import React from "react";
import { UserType } from "muminsmart-utils";
import {
  Home,
  User2,
  LayoutGrid,
  WalletMinimal,
  ShoppingBasket,
  Store,
  ChartNoAxesCombined,
} from "lucide-react";
import { routes } from "@/lib/constants";
import { getHomeRouteForUser } from "@/lib/routing";
import { Link, useLocation } from "react-router-dom";
import { useSession } from "@/contexts/AuthContext";
import { useCartWishlist } from "@/lib/hooks/useCartWishlist";

// Shape of each bottom-nav entry (icon, label, route, optional badge count)
interface INavItem {
  label: string;
  icon: React.ElementType;
  link: string;
  badge?: number;
}

// Fixed bottom navigation bar shown on most authenticated pages
const HomeNav = () => {
  const { data: session } = useSession();
  const { pathname } = useLocation();
  const { cartCount } = useCartWishlist();

  // Home link differs by role: customer home vs seller dashboard area
  const homeRoute = React.useMemo(
    () => getHomeRouteForUser(session?.user),
    [session?.user],
  );

  // Build nav items based on user type; empty when logged out
  const navItems = React.useMemo(() => {
    if (!session?.user) return [];

    const items: INavItem[] = [
      { label: "Home", icon: Home, link: homeRoute },
      {
        label: "Categories",
        icon: LayoutGrid,
        link: routes.categories.index,
      },
    ];

    // Seller-specific shortcuts
    if (session?.user?.type === UserType.Seller) {
      items.push(
        {
          label: "Dashboard",
          icon: ChartNoAxesCombined,
          link: routes.seller.dashboard,
        },
        {
          label: "My Shop",
          icon: Store,
          link: routes.seller.myShop.index,
        },
        {
          label: "Wallet",
          icon: WalletMinimal,
          link: routes.wallet.index,
        },
      );
    }

    // Shared items for all authenticated users
    items.push(
      {
        label: "Cart",
        icon: ShoppingBasket,
        link: routes.cart,
        badge: cartCount || undefined,
      },
      { label: "Account", icon: User2, link: routes.profile.index },
    );
    return items;
  }, [session?.user, cartCount, homeRoute]);

  // Hide nav on focused flows where bottom bar would clutter the UI
  if (
    !navItems.length ||
    pathname === routes.cart ||
    pathname.startsWith(`${routes.products.index}`) ||
    pathname.startsWith(`${routes.seller.myShop.addProduct}`) ||
    pathname.startsWith(`${routes.seller.myShop.product.index}`) ||
    pathname.startsWith(`${routes.profile.myReferrals}`)
  )
    return null;

  return (
    <nav className="home-nav fixed bottom-[max(1rem,env(safe-area-inset-bottom))] left-1/2 z-20 flex -translate-x-1/2 items-center gap-1 rounded-full border border-white/50 bg-white/50 px-1 py-0.5 shadow-[0_4px_16px_rgba(15,69,80,0.14),0_2px_6px_rgba(0,0,0,0.06)] backdrop-blur-xl">
      {navItems.map((item) => {
        if (!pathname) return null;

        // Match exact route or any nested child route
        const isActive =
          pathname === item.link || pathname?.startsWith(`${item.link}/`);
        return (
          <Link
            key={item.label}
            to={item.link}
            aria-label={item.label}
            className={`relative flex items-center justify-center rounded-full p-2 transition-colors ${
              isActive ? "text-primary" : "text-foreground/35"
            }`}
            prefetch="intent"
          >
            <item.icon size={25} strokeWidth={isActive ? 2.25 : 1.5} />
            {item.badge ? (
              // Cap badge display at 9+ for large cart counts
              <span className="absolute top-0.5 right-0.5 flex size-4 items-center justify-center rounded-full bg-primary px-0.5 text-xs font-semibold leading-none text-primary-foreground">
                {item.badge > 9 ? "9+" : item.badge}
              </span>
            ) : null}
          </Link>
        );
      })}
    </nav>
  );
};

export default HomeNav;
